/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { PartnerTier } from "@cosmos/airtable-data";
import type {
	CosmosButtonProps,
	DirectoryCardLogo,
	DirectoryCardMetaItem,
	DirectoryCardTone,
} from "@cosmos/components";

/** Subset of TrainingPartner / ServicePartner that `buildPartnerLinks` reads. */
export interface PartnerLinkSource {
	websiteUrl?: string;
	email?: string;
	country?: string;
	languages?: string[];
}

export interface PartnerMessages {
	website: string;
	email: string;
}

export interface PartnerLinks {
	buttons: CosmosButtonProps[];
	meta: DirectoryCardMetaItem[];
}

/**
 * Build the `links` + `meta` rows shared by Training Partner and Service
 * Partner cards. Both card kinds map website/email to the links row and
 * country/languages to the meta row identically; Service Partner adds an
 * office-location entry via the optional `officeLocation` injection point
 * (so the helper-returned array isn't mutated by the caller).
 *
 * Localized labels are passed in so i18n stays in the adapter (where the
 * `useMessages` hook lives) rather than reaching into the helper.
 */
/**
 * Strip characters that have meaning inside a `mailto:` URI before
 * interpolating an Airtable-sourced email into an `<a href>`. CRLF / `?` /
 * `&` / `#` (header injection vectors), whitespace, and angle-brackets are
 * removed. Returns null if no plausible address remains.
 */
function sanitizeMailto(raw: string | undefined): string | null {
	if (!raw) return null;
	const cleaned = raw.replace(/[\s<>"'?&#\r\n\t]/g, "").trim();
	// Plausible-email guard: must contain `@` and at least one `.` after it.
	// Belt-and-braces vs the strip — a malformed Airtable cell shouldn't
	// produce a clickable `mailto:` with no recipient.
	if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleaned)) return null;
	return cleaned;
}

export function buildPartnerLinks(
	partner: PartnerLinkSource,
	messages: PartnerMessages,
	options?: { officeLocation?: string },
): PartnerLinks {
	const buttons: CosmosButtonProps[] = [];
	if (partner.websiteUrl) {
		buttons.push({
			variant: "primary",
			label: messages.website,
			href: partner.websiteUrl,
			icon: "ArrowUpRightFromSquare",
			iconPosition: "end",
		});
	}
	const safeEmail = sanitizeMailto(partner.email);
	if (safeEmail) {
		buttons.push({ variant: "outline", label: messages.email, href: `mailto:${safeEmail}` });
	}

	// Meta row composes country → (officeLocation if supplied) → languages.
	// Slot office between country and languages so the row reads as a
	// location stack. When country is absent, office shifts to the front;
	// rebuilding via spread keeps the helper's returned array immutable.
	const meta: DirectoryCardMetaItem[] = [];
	if (partner.country) {
		meta.push({ icon: "LocationDrop", label: partner.country });
	}
	if (options?.officeLocation) {
		meta.push({ icon: "Skyscraper", label: options.officeLocation });
	}
	if (partner.languages && partner.languages.length > 0) {
		meta.push({ icon: "Language", label: partner.languages.join(", ") });
	}

	return { buttons, meta };
}

/**
 * Tier → fallback icon when the partner has no uploaded logo. Academic gets
 * the graduation cap (matches the heading-section visual language); all
 * other tiers get the generic Users icon.
 */
export function tierFallbackIcon(tier: PartnerTier): "GraduationCap" | "Users" {
	return tier === "academic" ? "GraduationCap" : "Users";
}

/**
 * Tier → card tone. Shared by both Training Partner and Service Partner
 * adapters so a Bronze partner renders the same way regardless of which
 * page consumes it. Only the Gold tier gets the gold gradient treatment;
 * all other tiers (Bronze / Silver / Academic / unknown) fall back to the
 * neutral surface — the badge label communicates the tier.
 */
export function tierToTone(tier: PartnerTier): DirectoryCardTone {
	switch (tier) {
		case "gold":
		case "silver":
		case "bronze":
		case "no-badge":
			return tier;
		default:
			return "neutral";
	}
}

/**
 * Build the `DirectoryCardLogo` for a partner. Image variant when the
 * partner has an uploaded logo; tier-driven fallback icon otherwise.
 *
 * Logo image is decorative-of-adjacent-heading per WCAG H67 — the card
 * renders the partner name as the heading directly below, so a non-empty
 * `alt` here would announce the name twice. The empty alt rationale lives
 * here so both partner adapters consume the same WCAG-aligned mapping.
 */
export function buildPartnerLogo(logoUrl: string | undefined, tier: PartnerTier): DirectoryCardLogo {
	return logoUrl ? { kind: "image", src: logoUrl, alt: "" } : { kind: "icon", icon: tierFallbackIcon(tier) };
}

interface BadgeLabelMessages {
	bronze: string;
	silver: string;
	gold: string;
	academic: string;
	training: string;
}

/** Subset of the `@epic-mw/i18n` messages dictionary the adapters consume. */
type MessagesDict = Record<string, string>;

/**
 * Pull the five partner-tier badge labels out of a `useMessages()`
 * dictionary, falling back to English when keys are missing. Shared so the
 * i18n-key list lives in one place (a missing translator key only needs to
 * be added here, not in every adapter).
 */
export function partnerBadgeMessages(messages: MessagesDict): BadgeLabelMessages {
	return {
		bronze: messages["epic.cosmos.directory.card.partner.bronze"] ?? "Bronze partner",
		silver: messages["epic.cosmos.directory.card.partner.silver"] ?? "Silver partner",
		gold: messages["epic.cosmos.directory.card.partner.gold"] ?? "Gold partner",
		academic: messages["epic.cosmos.directory.card.partner.academic"] ?? "Academic partner",
		training: messages["epic.cosmos.directory.card.partner.training"] ?? "Training partner",
	};
}

/**
 * Map a `PartnerTier` to its localized badge label. Single source of truth
 * shared by Training Partner and Service Partner adapters so a partner's
 * tier always renders the same chip text regardless of which card consumes it.
 */
export function partnerBadgeLabel(tier: PartnerTier, messages: BadgeLabelMessages): string {
	switch (tier) {
		case "academic":
			return messages.academic;
		case "bronze":
			return messages.bronze;
		case "silver":
			return messages.silver;
		case "gold":
			return messages.gold;
		case "unknown":
		case "no-badge":
		default:
			return messages.training;
	}
}
